/**
 * Add-broker modal — every field is an open text input by default. The
 * Brokerage Name field shows live autocomplete suggestions as the user
 * types; **selecting** a suggestion locks the Address field to
 * read-only and binds the agent to the existing brokerage. **Not
 * selecting** a suggestion means the user is creating a brand-new
 * brokerage and keeps the field open.
 *
 * Address is a single free-text input rather than four separate
 * city/state/zip boxes — designers asked for a flatter shape in this
 * iteration. Loose inline validation only checks for a street number;
 * at submit time we parse the full string into the four backend
 * fields (`address`, `city`, `state`, `zip_code`) with a US-format
 * regex. If parsing fails — e.g. the user forgot the city — the
 * address field surfaces the format example as a structured error.
 *
 * Save behaviour:
 *   - If a suggestion was picked → POST /v2/brokers/ under the chosen
 *     brokerage_id.
 *   - Otherwise → parse the address → POST /v2/brokerages/ (with
 *     `email_domain` derived from the broker email) → use the new id
 *     to POST /v2/brokers/.
 *
 * The returned agent (with embedded `brokerage`) is handed back to the
 * caller so the parent Brokerage section auto-selects the new broker
 * email and fills the read-only display fields.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Mail01, MarkerPin01, X } from "@untitledui/icons";
import { z } from "zod";
import { Button, ButtonUtility, Dialog, Input, Modal, ModalOverlay } from "@tricura/atlas-ui";
import { createAgent, createBrokerage, formatBrokerageAddress, listBrokerages } from "../api";
import { type AgentRow, type BrokerageRow } from "../types/submission";
import { brokerNameFields } from "../utils/person-name";

/** The single example string we surface as both placeholder and hint
 *  text. Keeping it in one constant prevents drift between the
 *  "what to type" hint and the "what went wrong" error. */
const ADDRESS_EXAMPLE = "1234 Avenue Street, Lakewood, NJ 08701";

/** US street address parser. Splits the full single-input value into
 *  the four shapes the backend wants. Lenient on whitespace, strict on
 *  the three commas and the trailing two-letter state + five-digit ZIP
 *  (optionally with +4 extension). Returns `null` when the string
 *  doesn't match the expected layout. */
const FULL_ADDRESS_RE = /^(.+?),\s*([^,]+?),\s*([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?$/;

function parseUsAddress(s: string): { address: string; city: string; state: string; zip_code: string } | null {
    const m = s.trim().match(FULL_ADDRESS_RE);
    if (!m) return null;
    const [, street, city, state, zip] = m;
    if (!/\d/.test(street)) return null; // street must include a number
    return {
        address: street.trim(),
        city: city.trim(),
        state: state.toUpperCase(),
        zip_code: zip,
    };
}

/** Re-compose a stored brokerage into the single-input string we show
 *  inside the modal when the user picks an existing record. Uses the
 *  US convention `"Street, City, ST ZIP"` (state and zip joined by a
 *  space, not a comma — unlike `formatBrokerageAddress` which is the
 *  display helper used outside the modal). */
function joinFullUsAddress(b: BrokerageRow): string {
    const stateZip = [b.state, b.zip_code].filter((s) => s && s.trim() !== "").join(" ");
    return [b.address, b.city, stateZip].filter((s) => s && s.trim() !== "").join(", ");
}

/** Pull the domain out of a broker email — `jane@acme.com` → `acme.com`.
 *  Returns "" for an empty/malformed email so the caller can decide
 *  whether to omit `email_domain` from the create payload. */
function extractDomainFromEmail(email: string): string {
    const m = email.trim().toLowerCase().match(/@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/);
    return m ? m[1] : "";
}

const schema = z.object({
    /** Filled only when the user picked a suggestion. Empty = create new. */
    brokerage_id: z.string(),
    brokerageName: z.string(),
    /** Single full-address input; parsed into 4 backend fields at submit. */
    brokerageAddress: z.string(),
    name: z.string(),
    email: z.string(),
});

type FormData = z.infer<typeof schema>;

/** Validation pivot: `brokerage_id` truthy → only broker fields are
 *  enforced (the brokerage came from the backend already). Otherwise
 *  the inline-create rules apply: address must include a digit (street
 *  number) and pass the minimum length. The format example is
 *  surfaced as the error message itself so the user sees exactly what
 *  shape we expect. */
const refinedSchema = schema.superRefine((data, ctx) => {
    if (data.name.trim().length === 0) {
        ctx.addIssue({ code: "custom", path: ["name"], message: "Required" });
    } else if (!/[A-Za-z]/.test(data.name)) {
        ctx.addIssue({ code: "custom", path: ["name"], message: "Name must include letters" });
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email.trim())) {
        ctx.addIssue({ code: "custom", path: ["email"], message: "Invalid email" });
    }

    if (data.brokerage_id) {
        // Selected an existing brokerage — server-side data wins, no
        // need to validate the address shape.
        if (data.brokerageName.trim().length === 0) {
            ctx.addIssue({ code: "custom", path: ["brokerageName"], message: "Required" });
        }
        return;
    }

    if (data.brokerageName.trim().length < 2) {
        ctx.addIssue({ code: "custom", path: ["brokerageName"], message: "At least 2 characters" });
    }

    const addr = data.brokerageAddress.trim();
    if (addr.length < 5 || !/\d/.test(addr)) {
        ctx.addIssue({
            code: "custom",
            path: ["brokerageAddress"],
            message: `Example: ${ADDRESS_EXAMPLE}`,
        });
    }
});

interface AgentModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated?: (agent: AgentRow) => void;
}

export function AgentModal({ isOpen, onOpenChange, onCreated }: AgentModalProps) {
    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal>
                <Dialog>
                    {({ close }) => (
                        <AgentModalBody
                            close={close}
                            onCreated={(agent) => {
                                onCreated?.(agent);
                                close();
                            }}
                        />
                    )}
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

interface AgentModalBodyProps {
    close: () => void;
    onCreated: (agent: AgentRow) => void;
}

function AgentModalBody({ close, onCreated }: AgentModalBodyProps) {
    const methods = useForm<FormData>({
        resolver: zodResolver(refinedSchema),
        mode: "onTouched",
        defaultValues: {
            brokerage_id: "",
            brokerageName: "",
            brokerageAddress: "",
            name: "",
            email: "",
        },
    });
    const { control, handleSubmit, watch, setValue, getValues, setError, trigger } = methods;

    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<BrokerageRow[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const brokerageId = watch("brokerage_id");
    const brokerageName = watch("brokerageName");
    const isLocked = !!brokerageId;

    // Debounced server-side search. Fires while the user is typing a
    // not-yet-matched brokerage name; results render in the floating
    // suggestions popover. Server search is preferable to a 50-row
    // client cache because real brokerage lists run into the hundreds.
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (isLocked) {
            setSuggestions([]);
            return;
        }
        const q = brokerageName.trim();
        if (q.length < 1) {
            setSuggestions([]);
            return;
        }
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            listBrokerages({ search: q, limit: 8 })
                .then((rows) => {
                    if (getValues("brokerageName").trim() !== q) return;
                    setSuggestions(rows);
                    setActiveIndex(-1);
                })
                .catch(() => {
                    setSuggestions([]);
                });
        }, 200);
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [brokerageName, isLocked, getValues]);

    const selectSuggestion = useCallback(
        (b: BrokerageRow) => {
            setValue("brokerage_id", b.id, { shouldValidate: true });
            setValue("brokerageName", b.name, { shouldValidate: false });
            setValue("brokerageAddress", joinFullUsAddress(b), { shouldValidate: false });
            setShowSuggestions(false);
            setSuggestions([]);
            // Clear any stale "Include a street number" error from the
            // previous create-new state.
            trigger(["brokerageAddress"]);
        },
        [setValue, trigger],
    );

    const handleNameChange = useCallback(
        (value: string) => {
            setValue("brokerageName", value, { shouldValidate: true });
            // If we were locked to an existing brokerage and the user
            // is editing the name, unlock and clear the auto-filled
            // address so it's obvious we're now creating new.
            if (brokerageId) {
                setValue("brokerage_id", "", { shouldValidate: false });
                setValue("brokerageAddress", "", { shouldValidate: false });
            }
            setShowSuggestions(true);
        },
        [brokerageId, setValue],
    );

    const onKeyDownName = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    const onSubmit = handleSubmit(async (data) => {
        setSubmitting(true);
        setServerError(null);
        try {
            let brokerageIdToUse = data.brokerage_id;

            if (!brokerageIdToUse) {
                // Loose inline validation already required a digit
                // + minimum length, but for the backend we need the
                // address split into 4 fields. If we can't parse,
                // surface the format example as the error and abort.
                const parsed = parseUsAddress(data.brokerageAddress);
                if (!parsed) {
                    setError("brokerageAddress", {
                        type: "custom",
                        message: `Use the format: ${ADDRESS_EXAMPLE}`,
                    });
                    setSubmitting(false);
                    return;
                }
                const domain = extractDomainFromEmail(data.email);
                const created = await createBrokerage({
                    name: data.brokerageName.trim(),
                    address: parsed.address,
                    city: parsed.city,
                    state: parsed.state,
                    zip_code: parsed.zip_code,
                    // Optional on the backend, but it's the dedupe key
                    // (email_domain + zip_code) so we send it whenever
                    // the email yields one.
                    email_domain: domain || undefined,
                });
                brokerageIdToUse = created.id;
            }

            // Single name input → stored `name` plus a real first/last
            // split (OR-1600). A one-word name stores a first name only.
            const agent = await createAgent({
                brokerage_id: brokerageIdToUse,
                ...brokerNameFields(data.name),
                email: data.email.trim(),
            });

            onCreated(agent);
        } catch (error) {
            setServerError(error instanceof Error ? error.message : "Failed to save broker");
        } finally {
            setSubmitting(false);
        }
    });

    return (
        <form
            onSubmit={onSubmit}
            className="bg-primary ring-primary flex w-[440px] flex-col rounded-xl ring-1"
        >
            <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                <h3 className="text-primary text-lg font-semibold">Brokerage</h3>
                <ButtonUtility color="tertiary" size="sm" icon={X} onClick={close} tooltip="Close" data-autotest="uw-information-brokerage-add-new-close-button" />
            </header>

            <div className="flex flex-col gap-4 px-6">
                <Controller
                    control={control}
                    name="brokerageName"
                    render={({ field, fieldState }) => (
                        <div className="relative">
                            <Input
                                size="md"
                                label="Type or Select Brokerage"
                                placeholder="Brokerage name"
                                isInvalid={fieldState.invalid}
                                hint={fieldState.error?.message}
                                value={field.value}
                                onChange={handleNameChange}
                                onFocus={() => setShowSuggestions(true)}
                                // Delay the hide so a click on a
                                // suggestion fires its onClick before
                                // the popover unmounts.
                                onBlur={() => {
                                    setTimeout(() => setShowSuggestions(false), 150);
                                    field.onBlur();
                                }}
                                onKeyDown={onKeyDownName}
                                data-autotest="uw-information-brokerage-add-new-brokerage-name-input"
                            />
                            {showSuggestions && !isLocked && suggestions.length > 0 && (
                                <ul
                                    role="listbox"
                                    className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-none"
                                >
                                    {suggestions.map((b, idx) => (
                                        <li key={b.id} role="option" aria-selected={idx === activeIndex}>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => selectSuggestion(b)}
                                                data-autotest="uw-information-brokerage-add-new-brokerage-option"
                                                className={`block w-full cursor-pointer px-3 py-2 text-left transition duration-100 ease-linear ${
                                                    idx === activeIndex ? "bg-primary_hover" : "hover:bg-primary_hover"
                                                }`}
                                            >
                                                <div className="text-primary text-sm font-medium">{b.name}</div>
                                                <div className="text-tertiary text-xs">
                                                    {formatBrokerageAddress(b) || "—"}
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {isLocked && (
                                <p className="text-tertiary mt-1 inline-flex items-center gap-1 text-xs">
                                    <Check aria-hidden="true" className="text-fg-success-primary size-3.5" />
                                    Using existing brokerage — edit the name to create a new one
                                </p>
                            )}
                        </div>
                    )}
                />

                <Controller
                    control={control}
                    name="brokerageAddress"
                    render={({ field, fieldState }) => (
                        <Input
                            size="md"
                            label="Brokerage Address"
                            placeholder={ADDRESS_EXAMPLE}
                            icon={MarkerPin01}
                            // Hint stays empty by default — the format
                            // example only surfaces as the error
                            // message itself when validation fails.
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            isReadOnly={isLocked}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-brokerage-add-new-brokerage-address-input"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState }) => (
                        <Input
                            size="md"
                            label="Broker Name"
                            placeholder="Add Broker Name"
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-brokerage-add-new-broker-name-input"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                        <Input
                            size="md"
                            type="email"
                            label="Broker Email"
                            placeholder="Add Broker Email"
                            icon={Mail01}
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-brokerage-add-new-broker-email-input"
                        />
                    )}
                />

                {serverError && <p className="text-error-primary text-sm">{serverError} </p>}
            </div>

            <div className="flex gap-3 px-6 py-6">
                <Button color="secondary" size="md" className="flex-1" onClick={close} isDisabled={submitting} data-autotest="uw-information-brokerage-add-new-cancel-button">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                    className="flex-1"
                    isLoading={submitting}
                    showTextWhileLoading
                    data-autotest="uw-information-brokerage-add-new-save-button"
                >
                    Save
                </Button>
            </div>
        </form>
    );
}
